import { useEffect, useRef } from "react";

/**
 * Re-run a page's loader when the user comes back to the app.
 *
 * On a phone the app is almost never closed — it is backgrounded. The tab keeps
 * its React tree alive, so the mount effects that fetched the data never run
 * again, and someone returning an hour later is shown the figures from whenever
 * they last opened it. Only force-quitting the browser fixed it, which is exactly
 * the complaint this hook exists to answer.
 *
 * Four events mean "they are looking at this again":
 *   - `visibilitychange` to visible — switched back to the tab or unlocked the phone
 *   - `focus` — the desktop equivalent, and some mobile browsers fire only this
 *   - `pageshow` with `persisted` — restored from the back/forward cache, where no
 *     effect re-runs at all and the DOM is handed back exactly as it was
 *   - `online` — the network returned, so requests that failed while away can succeed
 *
 * Throttled by `minIdleMs`: flicking to another app for two seconds should not
 * re-issue every request on the page. A bfcache restore bypasses the throttle,
 * because that path only fires when the page really was left.
 *
 * `reload` is read through a ref, so a loader redefined on every render (the usual
 * case) does not tear down and re-attach the listeners each time.
 */
export default function useRefreshOnReturn(reload, { minIdleMs = 30000 } = {}) {
  const reloadRef = useRef(reload);
  reloadRef.current = reload;
  // Seeded at mount: the mount effect has just fetched, so returning to the tab a
  // second later should not immediately fetch again.
  const lastRun = useRef(Date.now());

  useEffect(() => {
    const run = () => {
      if (document.visibilityState !== "visible") return;
      const now = Date.now();
      if (now - lastRun.current < minIdleMs) return;
      lastRun.current = now;
      if (reloadRef.current) reloadRef.current();
    };
    const onPageShow = (e) => {
      if (!e.persisted) return;
      lastRun.current = 0; // restored from bfcache — always stale, never throttle
      run();
    };

    document.addEventListener("visibilitychange", run);
    window.addEventListener("focus", run);
    window.addEventListener("pageshow", onPageShow);
    window.addEventListener("online", run);
    return () => {
      document.removeEventListener("visibilitychange", run);
      window.removeEventListener("focus", run);
      window.removeEventListener("pageshow", onPageShow);
      window.removeEventListener("online", run);
    };
  }, [minIdleMs]);
}
